import React, { useState, useEffect } from "react";
import axios from "axios";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import { tableCellClasses } from "@mui/material/TableCell";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
// import { useQuery } from "react-query"; // optional, not used in this version
import BuySellDrawerUtil from "./BuySellDrawerUtil";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
	[`&.${tableCellClasses.head}`]: {
		backgroundColor: theme.palette.common.black,
		color: theme.palette.common.white,
	},
	[`&.${tableCellClasses.body}`]: {
		fontSize: 14,
	},
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
	maxWidth: "800px",
	margin: "20px auto",
}));

function ManualTrade() {
	const [cryptos, setCryptos] = useState([]); // List of fetched cryptos
	const [searchTerm, setSearchTerm] = useState("");
	const [page, setPage] = useState(1); // current page
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [hasMore, setHasMore] = useState(true); // track if more data is available

	const PER_PAGE = 20; // number of items per fetch

	const CRYPTO_API_URL = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=${PER_PAGE}&page=${page}&sparkline=false&price_change_percentage=24h&locale=en`;

	// Fetch data when page changes
	useEffect(() => {
		const fetchCryptos = async () => {
			setLoading(true);
			setError(null);
			try {
				const response = await axios.get(CRYPTO_API_URL);
				if (response.data.length < PER_PAGE) {
					setHasMore(false); // no more data to load
				} else {
					setHasMore(true);
				}
				// Append new data to existing list
				setCryptos((prev) => [...prev, ...response.data]);
			} catch (err) {
				console.error("Error fetching cryptos:", err);
				setError("Failed to fetch cryptocurrencies. Please try again.");
			} finally {
				setLoading(false);
			}
		};

		fetchCryptos();
	}, [page]); // fetch when page updates

	const handleSearch = (event) => {
		setSearchTerm(event.target.value);
	};

	const filteredCryptos = cryptos.filter((crypto) =>
		crypto.name.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleLoadMore = () => {
		if (hasMore && !loading) {
			setPage((prev) => prev + 1);
		}
	};

	return (
		<div className="w-[99%] mx-auto my-2">
			{/* Search input */}
			<TextField
				id="outlined-basic"
				label="Search Crypto"
				variant="outlined"
				fullWidth
				onChange={handleSearch}
				value={searchTerm}
			/>

			{/* Show loader */}
			{loading && (
				<Box display="flex" justifyContent="center" my={2}>
					<CircularProgress />
				</Box>
			)}

			{/* Show error */}
			{error && (
				<Typography color="error" align="center" my={2}>
					{error}
				</Typography>
			)}

			{/* List of cryptos */}
			<ul className="w-[99%]">
				{filteredCryptos.map((crypto) => (
					<li key={crypto.id}>
						<BuySellDrawerUtil
							coinName={crypto.name}
							coinSymbol={crypto.symbol}
							coinPrice={crypto.current_price}
							coinId={crypto.id}
							coinImage={crypto.image}
							marketCap={crypto.market_cap}
							circulatingSupply={crypto.circulating_supply}
							high24h={crypto.high_24h}
							low24h={crypto.low_24h}
							coinRank={crypto.market_cap_rank}
							coinPriceChange24h={crypto.price_change_24h}
							coinPriceChangePercentage24h={crypto.price_change_percentage_24h}
							marketCapChange24h={crypto.market_cap_change_24h}
							marketCapChangePercentage24h={
								crypto.market_cap_change_percentage_24h
							}
							lastUpdated={crypto.last_updated}
							totalSupply={crypto.total_supply}
							maxSupply={crypto.max_supply}
							totalVolume={crypto.total_volume}
							athPrice={crypto.ath}
							athChangePercentage={crypto.ath_change_percentage}
							athDate={crypto.ath_date}
							atlPrice={crypto.atl}
							atlChangePercentage={crypto.atl_change_percentage}
							atlDate={crypto.atl_date}
						/>
					</li>
				))}
			</ul>

			{/* "See More" button */}
			{!loading && hasMore && filteredCryptos.length > 0 && (
				<Box textAlign="center" my={3}>
					<Button variant="contained" onClick={handleLoadMore}>
						See More
					</Button>
				</Box>
			)}

			{/* Optional: if no results after search */}
			{!loading && filteredCryptos.length === 0 && (
				<Typography align="center" my={2}>
					No cryptocurrencies found.
				</Typography>
			)}
		</div>
	);
}

export default ManualTrade;
